import { useQuery, UseQueryOptions, UseQueryResult } from "react-query";

import { useOktaAuth } from "@okta/okta-react";

import { minutesToMilliseconds } from "./dateUtils";
import { buildQueryString } from "./urlUtils";
import { useEnvironment } from "./useEnvironment";

const fetchData = async <ResponseType>(url: string, accessToken?: string): Promise<ResponseType> => {
  const options: RequestInit = accessToken
    ? {
        headers: {
          Authorization: "Bearer " + accessToken,
        },
      }
    : {};

  const res = await fetch(url, options);
  let json: any;

  try {
    json = await res.json();
  } catch {
    // unable to parse json response
  }

  if (400 <= res.status && res.status <= 599) {
    const errorText = json?.message || json?.error || res.statusText;
    throw new Error(errorText);
  }

  return json;
};

export const createGetRequest =
  <RequestType, ResponseType>(url: string) =>
  async (requestBody?: RequestType, accessToken?: string) => {
    const queryString = !!requestBody ? buildQueryString(requestBody) : "";
    const fullUrl = `${url}?${queryString}`;
    return fetchData<ResponseType>(fullUrl, accessToken);
  };

const optionsBase: Partial<UseQueryOptions<any, any, any>> = {
  staleTime: minutesToMilliseconds(3),
  refetchOnWindowFocus: false,
};

export const useGetRequestBase = <RequestType, ResponseType, ErrorType = unknown>(
  cacheKey: string,
  requestFunc: (requestBody: RequestType, accessToken?: string) => Promise<ResponseType>,
  requestBody: RequestType | undefined,
  accessToken?: string,
  options?: UseQueryOptions<ResponseType, ErrorType>,
): UseQueryResult<ResponseType, ErrorType> => {
  const fullCacheKey = [cacheKey, requestBody];

  const myQuery = useQuery<ResponseType, ErrorType>(fullCacheKey, async () => requestFunc(requestBody, accessToken), {
    ...optionsBase,
    ...options,
  });
  return myQuery;
};

export const useGetRequest = <RequestType, ResponseType, ErrorType = unknown>(
  cacheKey: string,
  requestFunc: (requestBody: RequestType, accessToken?: string) => Promise<ResponseType>,
  requestBody: RequestType | undefined,
  options?: UseQueryOptions<ResponseType, ErrorType>,
): UseQueryResult<ResponseType, ErrorType> => {
  const { authState } = useOktaAuth();
  const accessToken = authState?.accessToken?.accessToken || "";

  const { isLocal, isDev } = useEnvironment();
  const optionsWithRetry = {
    retry: isLocal || isDev ? false : 3, // do not retry in local
    ...options,
  };

  return useGetRequestBase(cacheKey, requestFunc, requestBody, accessToken, optionsWithRetry);
};
